/* eslint-disable react/require-default-props, react/jsx-one-expression-per-line */
import { Box, HStack, Text, VStack } from '@chakra-ui/core';
import moment from 'moment-timezone';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiClock } from 'react-icons/fi';
import { useTimezone } from '../hooks/UseTimezone';

interface InterviewTimezoneTimeProps {
  startTime: Date;
  endTime: Date;
  isShort?: boolean;
  size?: 'default' | 'compact';
}

export const InterviewTimezoneTime = memo(function InterviewTimezoneTime({
  startTime,
  endTime,
  isShort = false,
  size = 'default',
}: InterviewTimezoneTimeProps) {
  const { i18n } = useTranslation();

  const { primaryTz, secondaryTz, isCalendarTzEnabled } = useTimezone();

  const timeFormat = i18n.language === 'fr' ? 'LT' : 'hh:mm A';

  const isCompact = size === 'compact';

  const primaryFontSize = isCompact ? '0.65rem' : 'sm';
  const secondaryFontSize = isCompact ? '0.65rem' : 'xs';

  const badgeFontSize = isCompact ? '9px' : '0.65rem';

  const timezoneFontSize = isCompact ? '0.65rem' : 'xs';

  const iconSize = isCompact ? 12 : 15;

  const verticalSpacing = isCompact ? 0 : 1;

  const svgWidth = isCompact ? 10 : 15;
  const svgHeight = isCompact ? 8 : 16;
  const svgStrokeWidth = isCompact ? 2 : 4;

  const svgPath = `M 0 0 V ${svgHeight - svgStrokeWidth} Q 0 ${svgHeight} 6 ${svgHeight} H ${svgWidth - svgStrokeWidth}`;

  if (!isCalendarTzEnabled) {
    return (
      <Text fontSize={primaryFontSize}>
        {moment(startTime).format(timeFormat)} - {moment(endTime).format(timeFormat)}
      </Text>
    );
  }

  const primaryStart = moment.tz(startTime, primaryTz?.name || 'UTC').format(timeFormat);

  const primaryEnd = moment.tz(endTime, primaryTz?.name || 'UTC').format(timeFormat);

  const secondaryStart = moment.tz(startTime, secondaryTz?.name || 'UTC').format(timeFormat);

  const secondaryEnd = moment.tz(endTime, secondaryTz?.name || 'UTC').format(timeFormat);

  return (
    <HStack alignItems="flex-start" spacing={2}>
      {/* LEFT */}
      <VStack spacing={0} alignItems="center">
        <Box my="2px">
          <FiClock size={iconSize} color="#718096" />
        </Box>

        <Box position="relative">
          <svg
            width={svgWidth}
            height={svgHeight}
            style={{
              position: 'absolute',
              overflow: 'visible',
            }}
          >
            <path
              d={svgPath}
              fill="none"
              stroke="#A0AEC0"
              strokeWidth="1.5"
              strokeDasharray="2 4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </VStack>

      {/* RIGHT */}
      <VStack alignItems="flex-start" spacing={verticalSpacing}>
        {/* PRIMARY */}
        <HStack spacing={2}>
          <Text fontSize={primaryFontSize} fontWeight={size === 'compact' ? 'bold' : '600'}>
            {primaryStart} - {primaryEnd}
          </Text>

          <Box
            bg="blue.100"
            color="blue.600"
            px={isCompact ? 1 : 2}
            py="1px"
            borderRadius="md"
            fontSize={badgeFontSize}
            fontWeight="700"
            lineHeight={1.2}
          >
            {primaryTz?.label}
          </Box>

          {!isShort && (
            <Text fontSize={timezoneFontSize} color="blue.500">
              {primaryTz?.name}
            </Text>
          )}
        </HStack>

        {/* SECONDARY */}
        <HStack spacing={2}>
          <Text fontSize={secondaryFontSize} color="gray.700">
            {secondaryStart} - {secondaryEnd}
          </Text>

          <Box
            bg="gray.50"
            color="gray.600"
            px={isCompact ? 1 : 2}
            py="1px"
            borderRadius="md"
            fontSize={badgeFontSize}
            fontWeight="700"
            lineHeight={1.2}
          >
            {secondaryTz?.label}
          </Box>

          {!isShort && (
            <Text fontSize={timezoneFontSize} color="gray.500">
              {secondaryTz?.name}
            </Text>
          )}
        </HStack>
      </VStack>
    </HStack>
  );
});
